import React, { useRef, useCallback } from 'react';
import { SlapId, PlaceId, StickerDesignId, FocusedPlace } from '../utils/Primitives';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { getOrLoadSlap, lookupSlap } from '../slaps/Slaps';
import { getStickerUrl, getStickerDesign } from '../stickers/StickerDesigns';
import { fetchPlayer } from '../player/Players';
import AddressDisplay from '../components/AddressDisplay';

interface SlapActivityProps {
  slapId: SlapId;
  placeId: PlaceId;
  stickerId: StickerDesignId;
  size: number;
  timestamp: number;
  imageCID: string;
  onSlapClick: (slapId: SlapId) => void;
  isIndexing?: boolean;
  indexingStatus?: string;
  showProgress?: boolean;
  resourceStatus?: 'loading' | 'available' | 'error' | 'timeout' | null;
  setFocusOnPlace: (focusedPlace: FocusedPlace) => void;
  boardRef: React.MutableRefObject<any>;
}

const SlapActivity: React.FC<SlapActivityProps> = ({
  slapId,
  placeId,
  stickerId,
  size,
  timestamp,
  imageCID,
  onSlapClick,
  isIndexing = false,
  indexingStatus = '',
  showProgress = false,
  resourceStatus = null,
  setFocusOnPlace,
  boardRef,
}) => {
  const timeAgo = formatDistanceToNow(timestamp, { addSuffix: true });
  const lastClickTime = useRef<number>(0);

  // Synchronously check if we have the data
  const existingSlap = lookupSlap({ boardRef, slapId });
  const existingSticker = getStickerDesign({ boardRef, stickerId });
  const player = existingSlap ? fetchPlayer({ boardRef, address: existingSlap.player }) : null;

  // Only query if we're indexing and don't have the data
  const { isFetching } = useQuery({
    queryKey: ['slap', slapId],
    queryFn: () => getOrLoadSlap({ boardRef, slapId }),
    enabled: isIndexing && !existingSlap,
    refetchInterval: isIndexing && !existingSlap ? 2000 : false,
    refetchIntervalInBackground: true,
  });

  const handleClick = useCallback(() => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTime.current;

    // If less than 3 seconds have passed since the last click, ignore this click
    if (timeSinceLastClick < 3000) {
      console.log('Click ignored - too soon since last click');
      return;
    }

    lastClickTime.current = now;

    // Only allow click if we have the data or we're not indexing
    if (!isIndexing || existingSlap) {
      onSlapClick(slapId);
      setFocusOnPlace({ placeId, source: 'activity' });
    }
  }, [isIndexing, existingSlap, slapId, onSlapClick, placeId, setFocusOnPlace]);

  // Show indexing state if we're indexing and don't have the data yet
  const showIndexing = !existingSlap;

  return (
    <div
      className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-blue-100 flex items-center justify-center overflow-hidden">
        {existingSticker ? (
          <img
            src={getStickerUrl(existingSticker)}
            alt="Sticker"
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-blue-600 text-lg">👋</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {showIndexing ? (
            <span className="flex items-center">
              <span className="animate-pulse mr-2">⏳</span>
              {indexingStatus || 'Indexing slap...'}
            </span>
          ) : (
            <span>
              Slap by {existingSlap ? (
                <AddressDisplay address={existingSlap.player} ensName={player?.ens} />
              ) : 'Unknown'}
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500">{timeAgo}</p>
      </div>
    </div>
  );
};

export default SlapActivity;
